// Asking the assistant: POST /api/assistant/messages and read its server-sent
// events as they arrive (docs/v3/ASSISTANT.md 9). EventSource cannot POST, so
// the body is read by hand.
import type { AssistantLinkDto } from "@convenor/shared/v3";

export type AskBody = { chatId?: string; text: string; fromPath: string; audience: "team" | "expert" };
export type AskDone = { messageId: string; links: AssistantLinkDto[]; answered: boolean; replaced?: boolean; text?: string };
export type AskHandlers = {
  chat(id: string): void;
  delta(text: string): void;
  tool(label: string): void;
  /** `replaced`: the streamed text is withdrawn; show `text` (a "no data" answer) instead. */
  done(e: AskDone): void;
  error(message: string): void;
};

const FALLBACK = "The assistant is not available right now.";

export async function askStream(body: AskBody, on: AskHandlers, signal?: AbortSignal): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/api/assistant/messages", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    if (!signal?.aborted) on.error(FALLBACK);
    return;
  }
  if (!res.ok || !res.body) {
    const err = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    on.error(err?.error?.message ?? FALLBACK);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  /** A stream that stops without done or error was cut off: say so. */
  let ended = false;
  const handle = (block: string) => {
    const event = /^event: ?(.*)$/m.exec(block)?.[1]?.trim();
    const raw = /^data: ?(.*)$/m.exec(block)?.[1];
    if (!event || raw === undefined) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (event === "chat") on.chat(String(data.id));
    else if (event === "delta") on.delta(String(data.text));
    else if (event === "tool") on.tool(String(data.label));
    else if (event === "done") {
      ended = true;
      on.done(data as AskDone);
    } else if (event === "error") {
      ended = true;
      on.error(String(data.message ?? FALLBACK));
    }
  };
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });
      let at: number;
      while ((at = buffer.indexOf("\n\n")) >= 0) {
        handle(buffer.slice(0, at));
        buffer = buffer.slice(at + 2);
      }
      if (done) break;
    }
    if (buffer.trim()) handle(buffer);
    if (!ended && !signal?.aborted) on.error(FALLBACK);
  } catch {
    if (!signal?.aborted) on.error(FALLBACK);
  }
}
